/* Altara REAL-WORLD PROOF slide (~17s) */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { Play, RotateCcw, Users, Building2, Landmark, Sparkles, Quote } from "lucide-react";

const CREAM = "#F5EFE6";

export default function WalletAltaraProofDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const autoplay = params.get("autoplay") !== "0";

  if (embed) {
    return (
      <div className="w-screen h-screen flex items-center justify-center px-8 py-8 overflow-hidden"
           style={{ background: "linear-gradient(160deg, #F5EFE6 0%, #EEF2F7 55%, #E2E8F0 100%)" }}
           data-testid="altara-proof-embed">
        <div className="w-full max-w-6xl">
          <ProofStage autoplay={autoplay} />
        </div>
        <SlideBrandMark />
      </div>
    );
  }
  return (
    <WalletShell activeTab="demos">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="altara-proof-demo">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ color: GOLD }}>Slide asset</div>
          <h1 className="font-display text-3xl font-semibold" style={{ color: NAVY }}>Real-world proof</h1>
        </div>
        <ProofStage autoplay />
      </div>
    </WalletShell>
  );
}

function SlideBrandMark() {
  return (
    <div className="absolute bottom-4 right-6 flex items-center gap-2 text-[10px] tracking-widest uppercase"
         style={{ color: NAVY, opacity: 0.55 }}>
      <div className="w-4 h-4 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
        <span className="font-display font-bold text-[9px]" style={{ color: GOLD }}>A</span>
      </div>
      altara.co · Consumer Intelligence Platform
    </div>
  );
}

function ProofStage({ autoplay }) {
  const [phase, setPhase] = useState(0); // 0 idle · 1 kpis · 2 branch · 3 story · 4 story-hi · 5 story-close
  const timers = useRef([]);
  const autoFired = useRef(false);
  const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const t = (ms, fn) => timers.current.push(setTimeout(fn, ms));

  const play = () => {
    if (phase > 0 && phase < 5) return;
    clear(); setPhase(1);
    t(6000, () => setPhase(2));
    t(9500, () => setPhase(3));
    t(12800, () => setPhase(4));
    t(15200, () => setPhase(5));
  };
  const reset = () => { clear(); setPhase(0); };

  useEffect(() => {
    if (!autoplay || autoFired.current) return;
    autoFired.current = true;
    setTimeout(play, 800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay]);

  useEffect(() => () => clear(), []);

  return (
    <div className="relative rounded-3xl bg-white p-8 lg:p-10 border border-slate-100 shadow-2xl overflow-hidden"
         style={{ minHeight: 620 }}>
      {/* Watermark eyebrow */}
      <div className="text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ color: GOLD }}>
        Real-world proof · not an MVP
      </div>

      <AnimatePresence mode="wait">
        {phase === 0 && (
          <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="pt-20">
            <div className="text-center">
              <h2 className="font-display text-4xl font-semibold" style={{ color: NAVY }}>
                Altara isn&apos;t an MVP.
              </h2>
              <div className="text-slate-600 mt-2">Press play.</div>
              <button onClick={play} data-testid="play-proof"
                      className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white"
                      style={{ background: NAVY }}>
                <Play className="w-4 h-4" /> Play
              </button>
            </div>
          </motion.div>
        )}

        {phase >= 1 && phase <= 2 && (
          <motion.div key="stats" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.45 }} className="pt-6">
            <motion.h2 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                       className="font-display text-5xl font-semibold" style={{ color: NAVY }}>
              Altara isn&apos;t an MVP.
            </motion.h2>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.5 }}
                        className="text-lg text-slate-600 mt-2">
              It&apos;s already being used in the real world.
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              <KpiCard Icon={Building2} value={8}   suffix="" label="Institutional pilots"
                       hint="banks · wealth managers · insurers" delay={0.9} />
              <KpiCard Icon={Users} value={432} suffix="" label="Investors onboarded"
                       hint="live wallets · governed decisions" delay={1.3} />
              <KpiCard Icon={Landmark} value={6}   suffix="" label="Age groups represented"
                       hint="18-24 · 25-34 · 35-44 · 45-54 · 55-64 · 65+" delay={1.7} />
            </div>

            <AnimatePresence>
              {phase === 2 && (
                <motion.div key="branch" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="mt-6 rounded-2xl border p-5 flex items-start gap-4"
                            style={{ background: CREAM, borderColor: `${GOLD}55` }}
                            data-testid="proof-branch">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: NAVY }}>
                    <Users className="w-5 h-5" style={{ color: GOLD }} />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>
                      In-branch module
                    </div>
                    <div className="font-display text-lg font-semibold mt-0.5" style={{ color: NAVY }}>
                      For elder consumers who still prefer human interaction.
                    </div>
                    <div className="text-[13px] text-slate-600 mt-1 leading-relaxed">
                      Available as an assisted in-branch experience — the adviser walks through
                      the trust journey with the consumer, side by side, on the same evidence trail.
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {phase >= 3 && (
          <motion.div key="story" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }} className="pt-6 max-w-4xl" data-testid="proof-story">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                   style={{ background: `${GOLD}22` }}>
                <Quote className="w-5 h-5" style={{ color: GOLD }} />
              </div>
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>
                  What an informed consumer has already surfaced
                </div>
                <div className="mt-3 space-y-3 text-slate-700 text-[14.5px] leading-relaxed">
                  <p>
                    After learning what to look for, a consumer began questioning an existing life-insurance
                    arrangement. Those questions surfaced concerns involving a financial adviser, a medical
                    practitioner and a third-party premium payer — within a suspected scheme believed to have
                    operated for decades.
                  </p>

                  <AnimatePresence>
                    {phase >= 4 && (
                      <motion.p key="hi" initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5 }}
                                className="font-semibold" style={{ color: NAVY }}>
                        The matter has since been referred to South Africa&apos;s Financial Intelligence
                        Centre for investigation.
                      </motion.p>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {phase >= 5 && (
                      <motion.div key="close" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.5 }}
                                  className="mt-4 pt-4 border-t border-slate-100">
                        <div className="text-[11px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>
                          What it reinforced for us
                        </div>
                        <p className="mt-2 font-display text-xl font-semibold leading-snug" style={{ color: NAVY }}>
                          Education didn&apos;t just inform the consumer.<br />
                          It surfaced risk that may otherwise have remained hidden.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute bottom-4 right-4">
        <button onClick={reset} data-testid="reset-proof"
                className="w-8 h-8 rounded-full bg-white/80 border border-slate-200 flex items-center justify-center"
                title="Reset">
          <RotateCcw className="w-3.5 h-3.5 text-slate-500" />
        </button>
      </div>
    </div>
  );
}

function KpiCard({ Icon, value, suffix, label, hint, delay }) {
  const n = useCountUp(value, 1400, delay * 1000);
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay, duration: 0.45 }}
                className="rounded-2xl border p-5"
                style={{ background: "white", borderColor: "#E2E8F0" }}
                data-testid={`kpi-${label.split(" ")[0].toLowerCase()}`}>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase font-semibold"
           style={{ color: NAVY, opacity: 0.6 }}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-display text-6xl font-semibold mt-2 tabular-nums" style={{ color: NAVY }}>
        {n}{suffix}
      </div>
      <div className="text-[11px] text-slate-500 mt-1">{hint}</div>
    </motion.div>
  );
}

function useCountUp(target, duration, delayMs) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf; let start = null;
    const startTs = performance.now() + delayMs;
    const step = (now) => {
      if (now < startTs) { raf = requestAnimationFrame(step); return; }
      if (start === null) start = now;
      const elapsed = now - start;
      const p = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, delayMs]);
  return v.toLocaleString();
}
